import React, { useState, useEffect, useMemo } from 'react';
import { 
  Zap, MessageSquare, Bell, Mail, Brain, Globe, Edit, 
  ArrowRight, ArrowDown, User, Users, Briefcase, Phone,
  CheckCircle, Clock, AlertCircle, ChevronRight, Eye, EyeOff,
  Workflow, GitBranch, Circle, Activity
} from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL : SCHÉMA WORKFLOW VISUEL
// ════════════════════════════════════════════════════════════════════════════════

const WorkflowSchema = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStage, setSelectedStage] = useState(null);
  const [showInactive, setShowInactive] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/pipeline-workflow/stages`);
      const data = await res.json();
      setStages(data.stages || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  // Organiser les étapes par catégorie/flux
  const organizedStages = useMemo(() => {
    const categories = {
      'Acquisition': ['lead', 'lead_nrp', 'lead_a_rappeler'],
      'Qualification': ['lead_r1_pris', 'confirmer_rdv', 'lead_annule_par_societe'],
      'R1 & Audit': ['r1_audit', 'lead_en_reflexion_suite_r1'],
      'Closing': ['lead_signe', 'lead_perdu_avant_r1', 'lead_perdu_suite_r1'],
      'Autres': []
    };

    const categorized = {};
    const usedIds = new Set();

    Object.entries(categories).forEach(([cat, ids]) => {
      categorized[cat] = stages.filter(s => ids.includes(s.stage_id));
      ids.forEach(id => usedIds.add(id));
    });

    // Ajouter les stages non catégorisés dans "Autres"
    categorized['Autres'] = stages.filter(s => !usedIds.has(s.stage_id));

    return categorized;
  }, [stages]);

  // Calculer les connexions entre étapes
  const connections = useMemo(() => {
    const conns = [];
    stages.forEach(stage => {
      const exitTriggers = stage.workflow?.exit_triggers || [];
      exitTriggers.forEach(trigger => {
        if (trigger.target_stage && trigger.enabled) {
          conns.push({
            from: stage.stage_id,
            to: trigger.target_stage,
            type: trigger.type,
            description: trigger.description,
            condition: trigger.condition
          });
        }
      });
    });
    return conns;
  }, [stages]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 border-4 border-purple-500/30 border-t-purple-500 rounded-full animate-spin"></div>
          <span className="text-slate-400">Chargement du schéma...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/25">
              <GitBranch className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Schéma du Pipeline</h2>
              <p className="text-slate-400">Vue d'ensemble des flux d'automatisation</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowInactive(!showInactive)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                showInactive 
                  ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' 
                  : 'bg-purple-500/20 text-purple-400 border border-purple-500/50'
              }`}
            >
              {showInactive ? <Eye size={16} /> : <EyeOff size={16} />}
              {showInactive ? 'Tout afficher' : 'Actifs uniquement'}
            </button>
          </div>
        </div>

        {/* Légende */}
        <div className="flex items-center gap-6 mt-6 pt-4 border-t border-slate-700/50">
          <span className="text-xs text-slate-500 uppercase tracking-wider">Légende :</span>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-emerald-400 shadow-sm shadow-emerald-400/50"></span>
            <span className="text-sm text-slate-400">Actif</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-amber-400 shadow-sm shadow-amber-400/50"></span>
            <span className="text-sm text-slate-400">En attente</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-slate-500"></span>
            <span className="text-sm text-slate-400">Désactivé</span>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <ArrowRight size={14} className="text-purple-400" />
            <span className="text-sm text-slate-400">Transition</span>
          </div>
        </div>
      </div>

      {/* Vue Schéma par Catégories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {Object.entries(organizedStages).map(([category, categoryStages]) => {
          if (categoryStages.length === 0) return null;
          
          return (
            <CategorySection
              key={category}
              category={category}
              stages={categoryStages}
              allStages={stages}
              connections={connections}
              selectedStage={selectedStage}
              onSelectStage={setSelectedStage}
              showInactive={showInactive}
            />
          );
        })}
      </div>

      {/* Vue Connexions Globales */}
      <ConnectionsOverview 
        stages={stages} 
        connections={connections}
        selectedStage={selectedStage}
        onSelectStage={setSelectedStage}
      />

      {/* Panneau de détails */}
      {selectedStage && (
        <StageDetailPanel 
          stage={stages.find(s => s.stage_id === selectedStage)}
          connections={connections}
          allStages={stages}
          onClose={() => setSelectedStage(null)}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : SECTION PAR CATÉGORIE
// ════════════════════════════════════════════════════════════════════════════════

const CategorySection = ({ category, stages, allStages, connections, selectedStage, onSelectStage, showInactive }) => {
  const categoryColors = {
    'Acquisition': { bg: 'from-blue-500/10 to-cyan-500/10', border: 'border-blue-500/30', icon: 'text-blue-400' },
    'Qualification': { bg: 'from-purple-500/10 to-indigo-500/10', border: 'border-purple-500/30', icon: 'text-purple-400' },
    'R1 & Audit': { bg: 'from-amber-500/10 to-orange-500/10', border: 'border-amber-500/30', icon: 'text-amber-400' },
    'Closing': { bg: 'from-emerald-500/10 to-green-500/10', border: 'border-emerald-500/30', icon: 'text-emerald-400' },
    'Autres': { bg: 'from-slate-500/10 to-slate-600/10', border: 'border-slate-500/30', icon: 'text-slate-400' }
  };

  const colors = categoryColors[category] || categoryColors['Autres'];

  return (
    <div className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-2xl p-5 backdrop-blur-sm`}>
      <div className="flex items-center gap-3 mb-4">
        <Workflow className={`w-5 h-5 ${colors.icon}`} />
        <h3 className="text-lg font-semibold text-white">{category}</h3>
        <span className="text-xs text-slate-500 bg-slate-800/50 px-2 py-0.5 rounded-full">
          {stages.length} étapes
        </span>
      </div>

      <div className="space-y-3">
        {stages.map((stage, index) => (
          <StageNode
            key={stage.stage_id}
            stage={stage}
            allStages={allStages}
            connections={connections}
            isSelected={selectedStage === stage.stage_id}
            onSelect={() => onSelectStage(stage.stage_id === selectedStage ? null : stage.stage_id)}
            showInactive={showInactive}
            isLast={index === stages.length - 1}
          />
        ))}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : NOEUD D'ÉTAPE
// ════════════════════════════════════════════════════════════════════════════════

const StageNode = ({ stage, allStages, connections, isSelected, onSelect, showInactive, isLast }) => {
  const workflow = stage.workflow || {};
  const entryActions = workflow.entry_actions || [];
  const exitTriggers = workflow.exit_triggers || [];

  // Compter les éléments actifs et en attente
  const activeActions = entryActions.filter(a => a.enabled);
  const pendingActions = entryActions.filter(a => !a.enabled && (a.type === 'sms' || a.type === 'email'));
  const inactiveActions = entryActions.filter(a => !a.enabled && a.type !== 'sms' && a.type !== 'email');

  const activeExits = exitTriggers.filter(t => t.enabled);

  // Trouver les destinations
  const destinations = exitTriggers
    .filter(t => t.enabled && t.target_stage)
    .map(t => {
      const targetStage = allStages.find(s => s.stage_id === t.target_stage);
      return {
        ...t,
        targetName: targetStage?.stage_name || t.target_stage,
        targetColor: targetStage?.stage_color || '#6B7280'
      };
    });

  return (
    <div className="relative">
      {/* Carte principale */}
      <div
        onClick={onSelect}
        className={`relative p-4 rounded-xl border-2 transition-all cursor-pointer group ${
          isSelected
            ? 'bg-slate-800 border-purple-500 shadow-lg shadow-purple-500/20'
            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-800/80'
        }`}
      >
        {/* En-tête avec couleur et nom */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div 
              className="w-4 h-4 rounded-full shadow-lg"
              style={{ 
                backgroundColor: stage.stage_color,
                boxShadow: `0 0 10px ${stage.stage_color}40`
              }}
            />
            <span className="font-semibold text-white">{stage.stage_name}</span>
          </div>
          <ChevronRight className={`w-4 h-4 text-slate-500 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
        </div>

        {/* Indicateurs d'automatisation */}
        <div className="flex flex-wrap gap-2">
          {/* Actions actives */}
          {activeActions.length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              <span className="text-xs font-medium text-emerald-400">{activeActions.length} action{activeActions.length > 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Actions en attente (SMS/Email désactivés) */}
          {pendingActions.length > 0 && showInactive && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/20 border border-amber-500/30">
              <Clock size={12} className="text-amber-400" />
              <span className="text-xs font-medium text-amber-400">{pendingActions.length} en attente</span>
            </div>
          )}

          {/* Actions inactives */}
          {inactiveActions.length > 0 && showInactive && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-600/50 border border-slate-600">
              <span className="w-2 h-2 rounded-full bg-slate-500"></span>
              <span className="text-xs text-slate-400">{inactiveActions.length} off</span>
            </div>
          )}

          {/* Transitions actives */}
          {activeExits.length > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/20 border border-purple-500/30">
              <ArrowRight size={12} className="text-purple-400" />
              <span className="text-xs font-medium text-purple-400">{activeExits.length} sortie{activeExits.length > 1 ? 's' : ''}</span>
            </div>
          )}

          {/* Aucune config */}
          {activeActions.length === 0 && activeExits.length === 0 && (
            <span className="text-xs text-slate-500 italic">Pas d'automatisation active</span>
          )}
        </div>

        {/* Détail des actions au survol/sélection */}
        {isSelected && (
          <div className="mt-4 pt-3 border-t border-slate-700 space-y-2">
            {/* Actions */}
            {entryActions.map(action => (
              <ActionMini key={action.id} action={action} />
            ))}

            {/* Destinations */}
            {destinations.length > 0 && (
              <div className="pt-2 mt-2 border-t border-slate-700/50">
                <span className="text-xs text-slate-500 uppercase tracking-wider">Transitions :</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {destinations.map(dest => (
                    <div 
                      key={dest.id}
                      className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-900/50 border border-slate-700"
                    >
                      <ArrowRight size={10} className="text-purple-400" />
                      <span 
                        className="w-2 h-2 rounded-full"
                        style={{ backgroundColor: dest.targetColor }}
                      />
                      <span className="text-xs text-slate-300">{dest.targetName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Flèche vers l'étape suivante */}
      {!isLast && (
        <div className="flex justify-center py-2">
          <ArrowDown size={16} className="text-slate-600" />
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : ACTION MINIATURE
// ════════════════════════════════════════════════════════════════════════════════

const ActionMini = ({ action }) => {
  const getActionConfig = (type) => {
    const configs = {
      sms: { icon: MessageSquare, color: 'text-blue-400', bg: 'bg-blue-500/20' },
      email: { icon: Mail, color: 'text-cyan-400', bg: 'bg-cyan-500/20' },
      notification: { icon: Bell, color: 'text-amber-400', bg: 'bg-amber-500/20' },
      christelle_ia: { icon: Brain, color: 'text-purple-400', bg: 'bg-purple-500/20' },
      webhook: { icon: Globe, color: 'text-emerald-400', bg: 'bg-emerald-500/20' },
      update_field: { icon: Edit, color: 'text-pink-400', bg: 'bg-pink-500/20' }
    };
    return configs[type] || { icon: Zap, color: 'text-slate-400', bg: 'bg-slate-500/20' };
  };

  const config = getActionConfig(action.type);
  const Icon = config.icon;
  const isActive = action.enabled;
  const isPending = !action.enabled && (action.type === 'sms' || action.type === 'email');

  return (
    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${
      isActive ? config.bg : 'bg-slate-800/50'
    } ${!isActive && 'opacity-60'}`}>
      {/* Indicateur de statut */}
      <span className={`w-1.5 h-1.5 rounded-full ${
        isActive ? 'bg-emerald-400' : isPending ? 'bg-amber-400' : 'bg-slate-500'
      }`}></span>
      
      <Icon size={12} className={isActive ? config.color : 'text-slate-500'} />
      <span className={`text-xs ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
        {action.type === 'sms' ? 'SMS' : 
         action.type === 'notification' ? 'Notif' :
         action.type === 'email' ? 'Email' :
         action.type === 'christelle_ia' ? 'IA' :
         action.type}
      </span>
      <span className={`text-xs ${isActive ? 'text-slate-400' : 'text-slate-600'}`}>
        → {action.target}
      </span>
      {action.delay_minutes > 0 && (
        <span className="text-xs text-slate-500 flex items-center gap-0.5">
          <Clock size={10} />
          {action.delay_minutes}m
        </span>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : VUE D'ENSEMBLE DES CONNEXIONS
// ════════════════════════════════════════════════════════════════════════════════

const ConnectionsOverview = ({ stages, connections, selectedStage, onSelectStage }) => {
  if (connections.length === 0) return null;

  return (
    <div className="bg-slate-800/30 border border-slate-700 rounded-2xl p-6">
      <div className="flex items-center gap-3 mb-6">
        <Activity className="w-5 h-5 text-purple-400" />
        <h3 className="text-lg font-semibold text-white">Flux des Transitions</h3>
        <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
          {connections.length} connexions actives
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {connections.map((conn, index) => {
          const fromStage = stages.find(s => s.stage_id === conn.from);
          const toStage = stages.find(s => s.stage_id === conn.to);
          
          if (!fromStage || !toStage) return null;

          return (
            <div 
              key={index}
              className={`flex items-center gap-3 p-3 rounded-xl bg-slate-900/50 border transition-all cursor-pointer ${
                selectedStage === conn.from || selectedStage === conn.to
                  ? 'border-purple-500/50 bg-purple-500/10'
                  : 'border-slate-700 hover:border-slate-600'
              }`}
              onClick={() => onSelectStage(conn.from)}
            >
              {/* Source */}
              <div className="flex items-center gap-2 flex-1">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: fromStage.stage_color }}
                />
                <span className="text-xs text-slate-300 truncate">{fromStage.stage_name}</span>
              </div>

              {/* Flèche avec type */}
              <div className="flex flex-col items-center gap-0.5">
                <ArrowRight size={14} className="text-purple-400" />
                <span className="text-[10px] text-slate-500">
                  {conn.type === 'auto' ? 'auto' : 
                   conn.type === 'semi_auto' ? 'semi' :
                   conn.type === 'conversational' ? 'conv' : 'man'}
                </span>
              </div>

              {/* Destination */}
              <div className="flex items-center gap-2 flex-1 justify-end">
                <span className="text-xs text-slate-300 truncate">{toStage.stage_name}</span>
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: toStage.stage_color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════════
// COMPOSANT : PANNEAU DE DÉTAILS
// ════════════════════════════════════════════════════════════════════════════════

const StageDetailPanel = ({ stage, connections, allStages, onClose }) => {
  if (!stage) return null;

  const workflow = stage.workflow || {};
  const entryTriggers = workflow.entry_triggers || [];
  const entryActions = workflow.entry_actions || [];
  const exitTriggers = workflow.exit_triggers || [];

  const incomingConnections = connections.filter(c => c.to === stage.stage_id);
  const outgoingConnections = connections.filter(c => c.from === stage.stage_id);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-auto shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="p-6 border-b border-slate-700"
          style={{ 
            background: `linear-gradient(135deg, ${stage.stage_color}20, transparent)` 
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-6 h-6 rounded-full shadow-lg"
                style={{ 
                  backgroundColor: stage.stage_color,
                  boxShadow: `0 0 20px ${stage.stage_color}50`
                }}
              />
              <div>
                <h3 className="text-xl font-bold text-white">{stage.stage_name}</h3>
                <span className="text-sm text-slate-400">{stage.stage_id}</span>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg text-slate-400 hover:text-white"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Flux entrant */}
          {incomingConnections.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
                <ArrowRight className="rotate-180" size={14} />
                Flux entrant ({incomingConnections.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {incomingConnections.map((conn, i) => {
                  const fromStage = allStages.find(s => s.stage_id === conn.from);
                  return (
                    <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700">
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: fromStage?.stage_color }} />
                      <span className="text-sm text-slate-300">{fromStage?.stage_name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Déclencheurs d'entrée */}
          <div>
            <h4 className="text-sm font-medium text-slate-400 mb-3">Déclencheurs d'entrée ({entryTriggers.length})</h4>
            <div className="space-y-2">
              {entryTriggers.map(trigger => (
                <div key={trigger.id} className={`flex items-center gap-3 p-3 rounded-lg border ${
                  trigger.enabled ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-slate-800/50 border-slate-700'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${trigger.enabled ? 'bg-emerald-400' : 'bg-slate-500'}`}></span>
                  <span className={`text-sm ${trigger.enabled ? 'text-slate-200' : 'text-slate-500'}`}>
                    {trigger.description}
                  </span>
                </div>
              ))}
              {entryTriggers.length === 0 && (
                <span className="text-sm text-slate-500 italic">Aucun déclencheur configuré</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div>
            <h4 className="text-sm font-medium text-slate-400 mb-3">Actions à l'entrée ({entryActions.length})</h4>
            <div className="space-y-2">
              {entryActions.map(action => {
                const isActive = action.enabled;
                const isPending = !action.enabled && (action.type === 'sms' || action.type === 'email');
                
                return (
                  <div key={action.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                    isActive ? 'bg-emerald-500/10 border-emerald-500/30' :
                    isPending ? 'bg-amber-500/10 border-amber-500/30' :
                    'bg-slate-800/50 border-slate-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${
                        isActive ? 'bg-emerald-400' : isPending ? 'bg-amber-400' : 'bg-slate-500'
                      }`}></span>
                      <span className={`text-sm font-medium ${
                        isActive ? 'text-slate-200' : 'text-slate-500'
                      }`}>
                        {action.type.toUpperCase()}
                      </span>
                      <span className="text-sm text-slate-400">→ {action.target}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      isActive ? 'bg-emerald-500/20 text-emerald-400' :
                      isPending ? 'bg-amber-500/20 text-amber-400' :
                      'bg-slate-700 text-slate-500'
                    }`}>
                      {isActive ? 'Actif' : isPending ? 'En attente' : 'Désactivé'}
                    </span>
                  </div>
                );
              })}
              {entryActions.length === 0 && (
                <span className="text-sm text-slate-500 italic">Aucune action configurée</span>
              )}
            </div>
          </div>

          {/* Flux sortant */}
          <div>
            <h4 className="text-sm font-medium text-slate-400 mb-3 flex items-center gap-2">
              <ArrowRight size={14} />
              Flux sortant ({outgoingConnections.length})
            </h4>
            <div className="space-y-2">
              {exitTriggers.map(trigger => {
                const toStage = allStages.find(s => s.stage_id === trigger.target_stage);
                return (
                  <div key={trigger.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                    trigger.enabled ? 'bg-purple-500/10 border-purple-500/30' : 'bg-slate-800/50 border-slate-700'
                  }`}>
                    <div className="flex items-center gap-3">
                      <span className={`w-2 h-2 rounded-full ${trigger.enabled ? 'bg-purple-400' : 'bg-slate-500'}`}></span>
                      <span className={`text-sm ${trigger.enabled ? 'text-slate-200' : 'text-slate-500'}`}>
                        {trigger.description}
                      </span>
                    </div>
                    {toStage && (
                      <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-800">
                        <ArrowRight size={12} className="text-purple-400" />
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: toStage.stage_color }} />
                        <span className="text-xs text-slate-300">{toStage.stage_name}</span>
                      </div>
                    )}
                  </div>
                );
              })}
              {exitTriggers.length === 0 && (
                <span className="text-sm text-slate-500 italic">Aucune transition configurée</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowSchema;
